import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JSON_TEMPLATE = [
  {
    name: 'Example Outfitter',
    description: 'Description of the outfitter',
    entity_type: 'outfitters_guides',
    location: 'Colorado',
    image_url: 'https://example.com/image.jpg',
    tags: ['hiking', 'beginner-friendly'],
    contact_email: 'guide@example.com',
    website: 'https://example.com',
    specialties: ['hiking', 'camping'],
    years_experience: 5,
    certifications: ['Wilderness First Responder'],
  },
];

const CSV_TEMPLATE_HEADERS = [
  'Location\nFull Address or Lat, Long',
  'Category',
  'Tags',
  'Title',
  'Description\n(150 char or less)',
  'URL',
  'Special Offer',
];

const CSV_TEMPLATE_ROW = [
  '"123 Pine Trail Rd, Boulder, CO 80301"',
  '"Campgrounds/Resorts"',
  '"hiking, family-friendly, pet-friendly"',
  '"Pine Trail Campground"',
  '"Beautiful family-friendly campground with hiking trails and mountain views."',
  '"https://example.com"',
  '"10% off first booking with code WELCOME10"',
];

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Bulk upload (CSV + JSON) and the auto-looping geocode backfill.
 *
 * The backfill loop runs `limit=15` batches sequentially under Nominatim's
 * 1-req/sec policy (~15 s per batch) until pending=0, the admin stops it,
 * or a batch fails permanently. Each batch is well under the K8s ingress
 * timeout. Wired into the post-CSV-upload flow so newly-added rows fill
 * in coordinates automatically.
 */
export function useAdminBulkUpload({
  token,
  refetchEntities,
  refetchStats,
}) {
  // CSV/JSON modal state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadMode, setBulkUploadMode] = useState('csv');
  const [csvFile, setCsvFile] = useState(null);
  const [bulkData, setBulkData] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);

  // Geocode backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const stopBackfillRef = useRef(false);

  // ---------- Geocode backfill loop ----------
  // Kicks off the server-side background task, then polls its status endpoint
  // for progress. Since the actual geocoding runs inside the FastAPI process,
  // closing the admin tab (or losing wifi) no longer strands rows — the loop
  // keeps running until pending=0.
  const runBackfillLoop = async (startPendingArg) => {
    if (backfilling) return;
    stopBackfillRef.current = false;
    setBackfilling(true);

    const startPending = startPendingArg ?? 0;
    setBackfillProgress({ total: startPending, done: 0, failed: 0, remaining: startPending });

    try {
      // Ask the server to start the background task (idempotent — if one is
      // already running, we just piggy-back on its progress).
      try {
        await axios.post(
          `${API}/admin/entities/backfill-coords/start`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
        );
      } catch (err) {
        console.warn('Failed to start background backfill:', err);
        toast.error('Could not start geocode task. Try again in a moment.');
        return;
      }

      // Poll every 3 s. Cap at 3000 iterations (~2.5 hours) so a runaway
      // never runs forever — matches the backend's 5000-row hard cap.
      let lastRemaining = null;
      let stallCount = 0;
      for (let i = 0; i < 3000; i++) {
        if (stopBackfillRef.current) break;

        let statusResp;
        try {
          statusResp = await axios.get(
            `${API}/admin/entities/backfill-coords/status`,
            { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
          );
        } catch (err) {
          // Transient poll errors shouldn't kill the whole loop — try again.
          console.warn('Backfill status poll failed:', err);
          await new Promise((r) => setTimeout(r, 3000));
          continue;
        }

        const status = statusResp.data || {};
        const totalStart = status.total_start || startPending || 0;
        const updated = status.updated || 0;
        const failed = status.failed || 0;
        const remaining = status.remaining || 0;

        setBackfillProgress({
          total: totalStart,
          done: updated,
          failed,
          remaining,
        });

        if (!status.running) break;

        // Guard against a stuck loop — if `remaining` doesn't change across
        // 6 polls (~18 s) something's wrong and we bail so the user sees it.
        if (lastRemaining !== null && remaining === lastRemaining) {
          stallCount += 1;
          if (stallCount >= 6) {
            toast.warning('Geocoding appears stalled — check the audit log.');
            break;
          }
        } else {
          stallCount = 0;
        }
        lastRemaining = remaining;

        await new Promise((r) => setTimeout(r, 3000));
      }

      // Final refresh so the UI reflects newly-geocoded rows.
      refetchEntities?.();
      refetchStats?.();

      // Grab one last status for the toast copy.
      try {
        const finalResp = await axios.get(
          `${API}/admin/entities/backfill-coords/status`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        const { updated = 0, failed = 0 } = finalResp.data || {};
        if (updated > 0) {
          toast.success(
            `Geocoded ${updated} entities${failed > 0 ? ` (${failed} unresolvable)` : ''}.`
          );
        } else if (failed > 0) {
          toast.warning(`${failed} addresses could not be geocoded.`);
        }
      } catch {
        // Silent — the UI already reflects progress.
      }
    } finally {
      setBackfilling(false);
      setTimeout(() => setBackfillProgress(null), 4000);
      stopBackfillRef.current = false;
    }
  };

  // NOTE: `stopBackfill` no longer aborts the server task — it just stops
  // polling on the client. The background loop finishes on its own. That's a
  // conscious trade-off: once you start it, we'd rather complete than leave
  // rows half-geocoded (and Nominatim gets 1 req/sec regardless).
  const stopBackfill = () => {
    stopBackfillRef.current = true;
    toast.info('Stopped watching. Geocoding continues in the background.');
  };

  const handleBackfillCoords = (startPending) => {
    if (backfilling) return;
    if (!window.confirm(
      'Geocode all entities that have an address but no map coordinates?\n\n' +
      "This runs on the server at ~1 row/sec (Nominatim policy) and keeps " +
      'going even if you close this tab. You can safely navigate away.'
    )) return;
    runBackfillLoop(startPending);
  };

  // ---------- CSV upload ----------
  const handleBulkUploadCSV = async (file) => {
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/entities/bulk-upload-csv`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      const { created_count = 0, error_count = 0, geocode_pending = 0 } = response.data || {};
      toast.success(`CSV Upload complete! Created ${created_count} entities`);
      if (error_count > 0) {
        toast.warning(`${error_count} rows had errors`);
        console.log('Upload errors:', response.data.errors);
      }
      refetchEntities?.();
      refetchStats?.();
      setShowBulkUploadModal(false);
      setCsvFile(null);

      if (geocode_pending > 0) {
        toast.info(`Auto-geocoding ${geocode_pending} entities in the background…`, { duration: 5000 });
        // Don't block on the loop; fire-and-forget
        runBackfillLoop(geocode_pending);
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      const detail =
        error.response?.data?.detail ||
        (error.code === 'ECONNABORTED'
          ? 'CSV upload timed out. Try a smaller file or split into batches.'
          : 'CSV upload failed');
      toast.error(detail);
    } finally {
      setBulkUploading(false);
    }
  };

  // ---------- JSON upload ----------
  const handleBulkUploadJSON = async () => {
    try {
      const parsed = JSON.parse(bulkData);
      const entities = Array.isArray(parsed) ? parsed : [parsed];
      const response = await axios.post(
        `${API}/admin/entities/bulk-upload`,
        { entities },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Uploaded: ${response.data.successful} successful, ${response.data.failed} failed`);
      if (response.data.errors?.length > 0) {
        console.log('Upload errors:', response.data.errors);
      }
      setShowBulkUploadModal(false);
      setBulkData('');
      refetchEntities?.();
      refetchStats?.();
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Invalid JSON format or upload failed');
    }
  };

  // ---------- Templates ----------
  const downloadJSONTemplate = () => {
    const blob = new Blob([JSON.stringify(JSON_TEMPLATE, null, 2)], { type: 'application/json' });
    triggerDownload(blob, 'entity_upload_template.json');
  };

  const downloadCSVTemplate = () => {
    const quotedHeaders = CSV_TEMPLATE_HEADERS.map((h) => `"${h}"`).join(',');
    const csvContent = `${quotedHeaders}\n${CSV_TEMPLATE_ROW.join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, 'entity_upload_template.csv');
  };

  return {
    // modal
    showBulkUploadModal,
    setShowBulkUploadModal,
    bulkUploadMode,
    setBulkUploadMode,
    csvFile,
    setCsvFile,
    bulkData,
    setBulkData,
    bulkUploading,
    // backfill
    backfilling,
    backfillProgress,
    runBackfillLoop,
    stopBackfill,
    handleBackfillCoords,
    // handlers
    handleBulkUploadCSV,
    handleBulkUploadJSON,
    downloadJSONTemplate,
    downloadCSVTemplate,
  };
}
